import { prefersReducedMotion } from '@theme/utilities';

/**
 * <gamba-rail> — grilla en escritorio, carrusel con scroll-snap en mobile.
 *
 * El paso de avance se lee del DOM (ancho del primer hijo + gap), así que
 * funciona igual con cualquier ancho de item sin configurarlo dos veces.
 *
 * Los dos atributos que emite son NEGATIVOS a propósito ([out-of-viewport],
 * [not-scrollable]): `morph.js` borra del nodo vivo todo atributo que no venga
 * del servidor, y con polaridad negativa esa pérdida degrada a permisivo.
 * El desarrollo del por qué (gate de capas de iOS, tradeoff con `Component`,
 * `%` vs `vw`) está en docs/estado-ejecucion.md, corrección 32.
 */

/* ---------------------------------------------------------------------------
   Observers compartidos: hay ~14 rieles por página, uno por instancia se
   multiplica por 14.
   --------------------------------------------------------------------------- */

/** @type {IntersectionObserver | null} */
let viewportObserver = null;

/** @type {ResizeObserver | null} */
let sizeObserver = null;

/**
 * Marca con [out-of-viewport] el riel que NO está en viewport, para que el CSS
 * le quite la capa de composición. Ver docs/estado-ejecucion.md, corrección 32.
 *
 * @returns {IntersectionObserver}
 */
function getViewportObserver() {
  viewportObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const rail = entry.target;
        if (!(rail instanceof GambaRail)) continue;

        rail.toggleAttribute('out-of-viewport', !entry.isIntersecting);
        // Al entrar, el track recién pasa a overflow: auto. Resincronizar.
        if (entry.isIntersecting) rail.sync();
      }
    },
    { rootMargin: '100px' }
  );

  return viewportObserver;
}

/**
 * Un resize toca los rieles de toda la página en un solo callback. Se mide
 * TODO primero y se escribe después: alternar lectura y escritura fuerza un
 * reflow sincrónico por riel.
 *
 * @returns {ResizeObserver}
 */
function getSizeObserver() {
  sizeObserver ??= new ResizeObserver((entries) => {
    /** @type {[GambaRail, ReturnType<GambaRail['measure']>][]} */
    const measured = [];

    for (const entry of entries) {
      const rail = entry.target;
      if (rail instanceof GambaRail) measured.push([rail, rail.measure()]);
    }

    for (const [rail, metrics] of measured) rail.paint(metrics);
  });

  return sizeObserver;
}

/* ---------------------------------------------------------------------------
   Componente
   --------------------------------------------------------------------------- */

class GambaRail extends HTMLElement {
  /** @type {number | null} */
  #rafId = null;
  /** @type {MutationObserver | null} */
  #contentObserver = null;

  /*
   * Refs perezosas, no cacheadas: `morphSection` reemplaza los hijos de la
   * sección sin desconectar el custom element, así que una ref cacheada queda
   * apuntando a un nodo desprendido. Ver docs/estado-ejecucion.md.
   */
  get track() {
    return this.querySelector('[data-rail-track]');
  }

  get thumb() {
    return this.querySelector('[data-rail-thumb]');
  }

  get prevButton() {
    return this.querySelector('[data-rail-prev]');
  }

  get nextButton() {
    return this.querySelector('[data-rail-next]');
  }

  connectedCallback() {
    /*
     * Los dos listeners van en el host, que sobrevive al morph, y son
     * idempotentes: `addEventListener` con la misma función bound no acumula
     * copias. `scroll` no burbujea pero sí baja por captura.
     */
    this.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
    this.addEventListener('click', this.#onClick);

    getViewportObserver().observe(this);
    getSizeObserver().observe(this);

    /*
     * `watch-content`: el contenido lo inyecta un tercero DESPUÉS del
     * connect (LoyaltyLion en `gamba-club-earn`). Ni el host ni el track
     * cambian de tamaño cuando llegan las cards, así que el ResizeObserver no
     * se entera y la barra / las flechas quedarían en "no scrolleable". Es
     * opt-in para no sumarle un observer a los ~14 rieles estáticos.
     */
    if (this.hasAttribute('watch-content') && this.track && !this.#contentObserver) {
      this.#contentObserver = new MutationObserver(() => {
        if (this.#rafId === null) this.#rafId = requestAnimationFrame(this.#frame);
      });
      this.#contentObserver.observe(this.track, { childList: true, subtree: true });
    }

    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('scroll', this.#onScroll, { capture: true });
    this.removeEventListener('click', this.#onClick);

    getViewportObserver().unobserve(this);
    getSizeObserver().unobserve(this);
    this.removeAttribute('out-of-viewport');
    this.#contentObserver?.disconnect();
    this.#contentObserver = null;

    // Sin esto, un frame ya agendado corre sync() sobre un nodo desprendido.
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /** Mide y pinta de una. Para el arranque y para cuando entra en viewport. */
  sync() {
    this.paint(this.measure());
  }

  /**
   * Todas las lecturas de layout juntas. No escribir nada acá.
   *
   * @returns {{ maxScroll: number, scrollLeft: number, visible: number } | null}
   */
  measure() {
    const track = this.track;
    if (!track) return null;

    const { scrollWidth, clientWidth, scrollLeft } = track;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);

    return {
      maxScroll,
      scrollLeft,
      visible: maxScroll > 0 ? clientWidth / scrollWidth : 1,
    };
  }

  /**
   * Todas las escrituras juntas, a partir de lo que midió measure().
   *
   * Cada escritura va precedida de la lectura del estado actual: el callback
   * del ResizeObserver llega acá, y mostrar/esconder la barra cambia el alto
   * del host, así que una escritura incondicional re-dispara el observer y
   * dispara el warning de loop.
   *
   * @param {{ maxScroll: number, scrollLeft: number, visible: number } | null} metrics
   */
  paint(metrics) {
    if (!metrics) return;

    const { maxScroll, scrollLeft, visible } = metrics;
    const scrollable = maxScroll > 1;

    // Negativo a propósito: si el morph lo borra, la barra queda visible de
    // más en vez de que el riel deje de scrollear.
    this.toggleAttribute('not-scrollable', !scrollable);

    const track = this.track;
    if (track) {
      /*
       * En mobile el track es una región scrolleable y tiene que ser
       * enfocable. En la grilla de escritorio no scrollea: dejar el tabindex
       * ahí son paradas de tab muertas. El markup lo emite igual para
       * funcionar sin JS.
       */
      if (scrollable !== track.hasAttribute('tabindex')) {
        if (scrollable) track.setAttribute('tabindex', '0');
        else track.removeAttribute('tabindex');
      }
    }

    const thumb = this.thumb;
    if (thumb instanceof HTMLElement) {
      // El thumb ocupa la fracción visible y se desplaza sobre el resto.
      const width = Math.min(100, visible * 100);
      const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;

      const nextWidth = `${width}%`;
      const nextOffset = `${progress * (100 - width)}%`;

      if (thumb.style.width !== nextWidth) thumb.style.width = nextWidth;
      if (thumb.style.marginInlineStart !== nextOffset) thumb.style.marginInlineStart = nextOffset;
    }

    /*
     * aria-disabled y no `disabled`: un botón deshabilitado sale del orden de
     * tabulación, así que el foco del usuario que acaba de accionar la flecha
     * se cae a <body> justo al llegar al extremo. slide() ya es no-op ahí.
     *
     * LTR-only a propósito: acá `scrollLeft` es negativo en RTL y las dos
     * flechas quedarían deshabilitadas. Gamba mantiene sólo es/en, los dos LTR.
     */
    setFlag(this.prevButton, 'aria-disabled', !scrollable || scrollLeft <= 1);
    setFlag(this.nextButton, 'aria-disabled', !scrollable || scrollLeft >= maxScroll - 1);
  }

  /**
   * Avanza o retrocede un item.
   *
   * @param {number} direction - 1 hacia adelante, -1 hacia atrás.
   */
  slide(direction) {
    const track = this.track;
    if (!track) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const target = Math.max(0, Math.min(maxScroll, track.scrollLeft + direction * this.#step));

    // No-op en el extremo: es la contraparte del aria-disabled.
    if (Math.abs(target - track.scrollLeft) < 1) return;

    track.scrollTo({
      left: target,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /** Ancho del primer item más el gap: el paso real, sin configurarlo dos veces. */
  get #step() {
    const track = this.track;
    if (!track) return 0;

    // Se baja por los envoltorios `display: contents` (no tienen caja: miden
    // 0) hasta el primer item real. Pasa cuando el contenido lo trae un
    // tercero con su propio wrapper, como LoyaltyLion en `gamba-club-earn`.
    let first = track.firstElementChild;
    while (first && getComputedStyle(first).display === 'contents') first = first.firstElementChild;
    if (!first) return track.clientWidth;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  #onScroll = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.matches('[data-rail-track]')) return;
    // En captura, el host de un riel externo ve el scroll del track de un riel
    // anidado: que cada uno maneje el suyo, igual que en #onClick.
    if (target.closest('gamba-rail') !== this) return;
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(this.#frame);
  };

  #frame = () => {
    this.#rafId = null;
    this.sync();
  };

  #onClick = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const control = target.closest('[data-rail-prev], [data-rail-next]');
    // Un riel anidado tiene sus propias flechas: que cada uno maneje las suyas.
    if (!control || control.closest('gamba-rail') !== this) return;
    if (control.getAttribute('aria-disabled') === 'true') return;

    this.slide(control.hasAttribute('data-rail-next') ? 1 : -1);
  };
}

/**
 * Escribe un atributo booleano-como-string sólo si cambió.
 *
 * @param {Element | null} element
 * @param {string} name
 * @param {boolean} value
 */
function setFlag(element, name, value) {
  if (!element) return;

  const next = String(value);
  if (element.getAttribute(name) !== next) element.setAttribute(name, next);
}

if (!customElements.get('gamba-rail')) {
  customElements.define('gamba-rail', GambaRail);
}
